import {
	FileBrowserAction,
	isRootNode,
	isSharedWithMeNode,
} from "@/ui/components/FileBrowser/types";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;

type HasAction = (action: FileBrowserAction) => boolean;

/** `*` stands for every ability, so an owner passes any check. */
const can = (file: FileDTO | null, ability: string): boolean =>
	!!file?.active_user_abilities &&
	(file.active_user_abilities.includes("*") ||
		file.active_user_abilities.includes(ability));

/** Owning a file is what `*` means: every ability, rather than a granted few. */
export const isFileOwner = (file: FileDTO | null): boolean =>
	!!file?.active_user_abilities && file.active_user_abilities.includes("*");

export type FileActionAvailability = {
	edit: boolean;
	share: boolean;
	toggleFavorite: boolean;
	remove: boolean;
	any: boolean;
};

export const fileActionAvailability = (
	file: FileDTO | null,
	hasAction: HasAction
): FileActionAvailability => {
	const edit = hasAction("update-file-meta") && can(file, "update");
	const share = hasAction("share-file") && isFileOwner(file);
	const toggleFavorite = hasAction("toggle-is-favorite") && isFileOwner(file);
	const remove = hasAction("delete-file") && can(file, "destroy");

	return {
		edit,
		share,
		toggleFavorite,
		remove,
		any: edit || share || toggleFavorite || remove,
	};
};

export type FolderActionAvailability = {
	edit: boolean;
	remove: boolean;
	any: boolean;
};

export const folderActionAvailability = (
	folder: ResourceTreeNodeDO | null,
	hasAction: HasAction
): FolderActionAvailability => {
	const isEditable =
		!!folder && !isSharedWithMeNode(folder) && !isRootNode(folder);

	const edit = isEditable && hasAction("update-folder");
	const remove = isEditable && hasAction("delete-folder");

	return { edit, remove, any: edit || remove };
};

export type RemovalConfirm = "none" | "file" | "folder" | "batch";

export type SelectionRemoval = {
	files: FileDTO[];
	folders: ResourceTreeNodeDO[];
	confirm: RemovalConfirm;
};

const removalConfirm = (
	fileCount: number,
	folderCount: number
): RemovalConfirm => {
	if (!fileCount && !folderCount) {
		return "none";
	}

	if (fileCount === 1 && !folderCount) {
		return "file";
	}

	if (folderCount === 1 && !fileCount) {
		return "folder";
	}

	return "batch";
};

export const selectionRemoval = (
	selectedFiles: FileDTO[],
	selectedFolders: ResourceTreeNodeDO[],
	hasAction: HasAction
): SelectionRemoval => {
	const files = selectedFiles.filter(
		(file) => fileActionAvailability(file, hasAction).remove
	);
	const folders = selectedFolders.filter(
		(folder) => folderActionAvailability(folder, hasAction).remove
	);

	return {
		files,
		folders,
		confirm: removalConfirm(files.length, folders.length),
	};
};
